import React, { FC } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import TicketDto from 'types/api/TicketDto';
import AutoSaveWatcher, { AutoSaveIndicator } from 'components/AutoSaveWatcher';
import { useAvailableRFPs, useRfpResponse, useTicketConnectedElement } from 'api';
import Tooltip from 'components/Tooltip/Tooltip';
import Loader from 'components/Loader/Loader';
import { HelpIcon } from 'components/SvgIcons';
import FormikSelectInput from 'components/forms/formik/FormikSelectInput/FormikSelectInput';
import FormikDatePickerInput from 'components/forms/formik/FormikDatePickerInput/FormikDatePickerInput';
import FormikTextAreaInput from 'components/forms/formik/FormikTextAreaInput/FormikTextAreaInput';
import TicketStatus from 'types/TicketStatus';
import { useLinkedCriterionUrl } from 'hooks/useLinkedCriterionUrl';

import ChangeTicketStatusButton from './components/ChangeTicketStatusButton/ChangeTicketStatusButton';
import TicketStatusIndicator from './components/TicketStatusIndicator/TicketStatusIndicator';
import useTicketAssignedUserOptions from './hooks/useTicketAssignedUserOptions';
import useInitialValues, { TicketFormState } from './hooks/useInitialValues';
import useTicketSubmit from './hooks/useTicketSubmit';
import useSchema from './hooks/useSchema';

import './TicketForm.scss';

export interface CommentFormProps {
  ticket: TicketDto;
}

const TicketForm: FC<CommentFormProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const initialValues = useInitialValues(ticket);
  const schema = useSchema();
  const [onSubmit, loading, isSuccess] = useTicketSubmit();

  const { data: ticketConnectedElement } = useTicketConnectedElement(ticket.id, !!ticket.traro_element_response_id);
  const { data: availableRFPs } = useAvailableRFPs(undefined);
  const { data: connectedRfpResponse } = useRfpResponse(ticketConnectedElement?.rfp_response_id);

  const assignedUsersOptions = useTicketAssignedUserOptions(
    availableRFPs,
    connectedRfpResponse,
    ticketConnectedElement,
  );

  const disabled = loading || ticket.ticket_status === TicketStatus.CLOSED;
  const linkedCriterionUrl = useLinkedCriterionUrl(ticketConnectedElement);

  return (
    <Formik<TicketFormState>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <AutoSaveWatcher>
        <Form className="enkrateia-ticket-form">
          <section className="header">
            <div className="max-width">
              <h2>
                {t('pages.ticketDetails.ticketNo')} {ticket.id}
              </h2>
              <ChangeTicketStatusButton ticket={ticket} />
            </div>
          </section>
          <AutoSaveIndicator />
          <section className="form-elements">
            <label>{t('common.tickets.status')}:</label>
            <TicketStatusIndicator status={ticket.ticket_status} />
            <label>{t('common.tickets.assignedTo')}:</label>
            <FormikSelectInput variant="filled" name="assignedTo" options={assignedUsersOptions} disabled={disabled} />
            <label>
              {t('common.tickets.dueDate')}
              <Tooltip tooltip={t('common.tickets.dueDateHelp')}>
                <HelpIcon color="#2D2C64" size={18} />
              </Tooltip>
              :
            </label>
            <FormikDatePickerInput noLabel variant="filled" name="dueDate" disabled={disabled} />
            {ticket.traro_element_response_id !== null && (
              <>
                <label>{t('common.tickets.linkedCriterion')}:</label>
                {!!ticketConnectedElement && <Link to={linkedCriterionUrl}>{ticketConnectedElement.element_name}</Link>}
                {ticketConnectedElement === undefined && <Loader />}
              </>
            )}
            <label>{t('common.tickets.ticketDescription')}:</label>
            <FormikTextAreaInput variant="filled" name="ticketDescription" disabled={disabled} rows={6} />
          </section>
          {isSuccess}
        </Form>
      </AutoSaveWatcher>
    </Formik>
  );
};

export default TicketForm;
